import { ChangeDetectionStrategy, Component, ElementRef, input, output, signal, viewChild } from '@angular/core';

export interface IDropzoneFile {
  name: string;
  meta?: string;
  previewUrl?: string | null;
  format?: 'square' | 'horizontal' | 'card' | 'seal';
  previewBg?: string;
  verified?: boolean;
}

/**
 * Upload Dropzone del DS: Default (arrastra o elige) · Dragging · Subiendo (progreso) · Uploaded (vista previa,
 * datos del archivo y quitar). El padre decide qué hacer con el archivo (validar, subir con presign+confirm).
 */
@Component({
  selector: 'app-dropzone',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <input #picker type="file" class="d-none" [accept]="accept()" [disabled]="disabled()" (change)="onPick($event)" />
    @if (file(); as f) {
      <div class="uploaded" [class.has-error]="!!error()">
        <div class="preview" [class]="'preview preview--' + (f.format || 'square')" [style.background]="f.previewBg || null">
          @if (f.previewUrl) { <img [src]="f.previewUrl" alt="" /> } @else { <cr-icon name="image" [size]="22" /> }
        </div>
        <div class="info">
          <p class="name">{{ f.name }}</p>
          @if (progress() !== null && progress()! < 100) {
            <p class="meta">Subiendo… {{ progress() }} %</p>
            <span class="cr-progress"><span [style.width.%]="progress()"></span></span>
          } @else {
            <p class="meta">{{ f.meta }}@if (f.verified) { <span class="ok">✓</span> }</p>
            @if (showBar()) { <span class="cr-progress cr-progress--success"><span style="width:100%"></span></span> }
          }
        </div>
        @if (replaceLabel()) {
          <button type="button" class="cr-btn cr-btn--secondary cr-btn--s" [disabled]="disabled()" (click)="open()">{{ replaceLabel() }}</button>
        }
        @if (removable()) {
          <button type="button" class="remove" [disabled]="disabled()" [attr.aria-label]="progress() !== null && progress()! < 100 ? 'Cancelar' : 'Quitar'" (click)="removed.emit()">
            <cr-icon name="trash" [size]="18" />
          </button>
        }
      </div>
    } @else {
      <div class="zone" [class.is-dragging]="dragging()" [class.is-compact]="compact()" [class.has-error]="!!error()" [class.is-disabled]="disabled()"
        (dragover)="onDragOver($event)" (dragleave)="dragging.set(false)" (drop)="onDrop($event)" (click)="open()"
        role="button" [attr.tabindex]="disabled() ? -1 : 0" [attr.aria-disabled]="disabled() || null" (keydown.enter)="open()" (keydown.space)="open(); $event.preventDefault()">
        <span class="bubble"><cr-icon name="upload" [size]="24" /></span>
        <p class="title">{{ title() }}</p>
        @if (hint()) { <p class="hint">{{ hint() }}</p> }
        <span class="cr-btn cr-btn--secondary cr-btn--s">{{ buttonLabel() }}</span>
      </div>
    }
    @if (error()) { <p class="err"><cr-icon name="alert" [size]="16" />{{ error() }}</p> }
  `,
  styles: [`
    :host{display:flex;flex-direction:column;gap:8px;min-width:0}
    .zone{border:2px dashed var(--cr-border-strong);border-radius:24px;background:#fff;padding:24px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;cursor:pointer;min-height:212px;transition:background var(--cr-motion-quick),border-color var(--cr-motion-quick)}
    .zone:hover,.zone.is-dragging{background:var(--cr-brand-teal-soft);border-color:var(--cr-brand-teal)}
    .zone.is-dragging .bubble{background:var(--cr-brand-teal);color:#fff}
    .zone.is-compact{min-height:0;padding:20px}
    .zone.has-error{border-color:var(--cr-danger)}
    .bubble{width:52px;height:52px;border-radius:50%;background:var(--cr-brand-teal-soft);color:var(--cr-brand-teal);display:grid;place-items:center}
    .title{margin:0;font:600 20px/24px var(--cr-font-display);letter-spacing:-.1px}
    .hint{margin:0;font:400 13px/18px var(--cr-font-body);color:var(--cr-text-tertiary)}
    .uploaded{display:flex;align-items:center;gap:16px;padding:24px;border:1px solid var(--cr-border-subtle);border-radius:24px;background:#fff;min-width:0}
    .uploaded.has-error{border-color:var(--cr-danger)}
    .preview{width:64px;height:64px;border-radius:16px;overflow:hidden;display:grid;place-items:center;flex:none;background:var(--cr-brand-teal-soft);color:var(--cr-brand-teal)}
    .preview--horizontal{width:132px;height:44px;border-radius:12px}
    .preview--card{width:88px;height:50px;border-radius:8px}
    .preview img{width:100%;height:100%;object-fit:cover}
    .preview--horizontal img,.preview--seal img{object-fit:contain}
    .info{flex:1;min-width:0;display:flex;flex-direction:column;gap:6px}
    .name{margin:0;font:600 15px/20px var(--cr-font-body);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .meta{margin:0;font:400 13px/18px var(--cr-font-body);color:var(--cr-text-secondary)}
    .ok{margin-left:4px;color:var(--cr-text-primary)}
    .remove{width:40px;height:40px;border-radius:12px;border:0;background:var(--cr-bg-surface-2);color:var(--cr-danger);display:grid;place-items:center;cursor:pointer;flex:none}
    .zone.is-disabled,.zone.is-disabled:hover{cursor:not-allowed;opacity:.55;background:#fff;border-color:var(--cr-border-strong)}
    .remove:disabled{opacity:.4;cursor:not-allowed}
    .err{display:flex;align-items:center;gap:6px;margin:0;font:400 13px/18px var(--cr-font-body);color:var(--cr-danger)}
    @media (max-width:640px){ .uploaded{padding:16px;gap:12px} .zone{min-height:180px} }
  `],
})
export class DropzoneComponent {
  readonly accept = input<string>('image/png');
  readonly title = input<string>('Arrastra tu archivo aquí');
  readonly hint = input<string>('');
  readonly buttonLabel = input<string>('Elegir archivo');
  readonly replaceLabel = input<string>('');
  readonly file = input<IDropzoneFile | null>(null);
  readonly progress = input<number | null>(null);
  readonly error = input<string | null>(null);
  readonly compact = input<boolean>(false);
  readonly removable = input<boolean>(true);
  readonly showBar = input<boolean>(true);
  /** Sin elegir, soltar, reemplazar ni quitar (p. ej. negocio inactivo en solo lectura). */
  readonly disabled = input<boolean>(false);
  readonly selected = output<File>();
  readonly removed = output<void>();

  readonly dragging = signal(false);
  private picker = viewChild<ElementRef<HTMLInputElement>>('picker');

  open(): void { if (!this.disabled()) this.picker()?.nativeElement.click(); }

  onPick(e: Event): void {
    const el = e.target as HTMLInputElement;
    const f = el.files?.[0];
    if (f) this.selected.emit(f);
    el.value = '';
  }

  onDragOver(e: DragEvent): void { e.preventDefault(); if (!this.disabled()) this.dragging.set(true); }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.dragging.set(false);
    if (this.disabled()) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) this.selected.emit(f);
  }
}
